/**
 * BB ID の付け直し(D-205)の書き込み。計算は bbReissue.ts。
 * 順番: ① まとめる元のマスタを指しているデータの書き換え(日次集計・シフト・トレーニング など)→ ② オペレーターマスタ(1回のまとめ書き)。
 * 途中で止まっても、もう一度実行すれば残りだけ書き換わる(①は「まとめる元のIDが残っているもの」だけを書き換えるため)。
 */
import { collection, deleteField, doc, getDocs, serverTimestamp, writeBatch, type DocumentData } from 'firebase/firestore';
import { getDb } from '@core/lib/firebase';
import { writeAuditLog } from '@core/lib/auditLog';
import { nextOperatorCode } from '@core/lib/numbering';
import type { Operator } from '@core/lib/types';
import { primaryStyle } from './operatorRepo';
import { reissuedAccounts, rewriteOpRefs, type ReissuePerson } from './bbReissue';

/** op_id を中に持つデータ(値の書き換え) */
const VALUE_COLLECTIONS = ['daily_summary', 'shifts', 'deliveries', 'targets', 'month_close', 'training_records', 'shift_change_requests', 'users', 'shinagawa_kintai'] as const;
/** docId に op_id が入っているデータ(新しいIDへ移す) */
const KEY_COLLECTIONS = [
  // training_tracks/{op_id}_{business}
  { name: 'training_tracks', split: (id: string) => splitPrefix(id) },
  // training_milestones/{op_id}_{business}_{h}。消せない(rules)ので写すだけ
  { name: 'training_milestones', split: (id: string) => splitPrefix(id), keepOld: true },
  // shift_submissions/{month}_{op_id}・shift_notices/{month}_{op_id}
  { name: 'shift_submissions', split: (id: string) => splitSuffix(id) },
  { name: 'shift_notices', split: (id: string) => splitSuffix(id) },
] as const;

export const REISSUE_COLLECTION_LABELS: Record<string, string> = {
  daily_summary: '日次集計',
  shifts: '出勤表',
  deliveries: '日報/サマリ',
  targets: '月次設定',
  month_close: '月次の締め',
  training_records: '面談・研修の記録',
  shift_change_requests: 'シフトの変更申請',
  users: 'ユーザー',
  shinagawa_kintai: '勤怠の手修正',
  training_tracks: 'トレーニング経過',
  training_milestones: 'トレーニングの到達記録',
  shift_submissions: 'シフト提出',
  shift_notices: 'シフト変更のお知らせ',
};

function splitPrefix(id: string): { op: string; rebuild: (op: string) => string } {
  const i = id.indexOf('_');
  return { op: i < 0 ? id : id.slice(0, i), rebuild: (op) => (i < 0 ? op : `${op}${id.slice(i)}`) };
}
function splitSuffix(id: string): { op: string; rebuild: (op: string) => string } {
  const i = id.indexOf('_');
  return { op: i < 0 ? '' : id.slice(i + 1), rebuild: (op) => `${id.slice(0, i)}_${op}` };
}

type Write = { kind: 'set'; col: string; id: string; data: DocumentData } | { kind: 'delete'; col: string; id: string };

export type RefPlan = {
  writes: Write[];
  /** 画面に出す件数(データの種類ごと) */
  counts: Record<string, number>;
  /** 書き換えずに残したもの(同じ人の分が両方にある) */
  conflicts: string[];
};

/** まとめる元 → 残すマスタ の対応 */
export function mergeMapOf(persons: ReissuePerson[]): Map<string, { to: string; name: string }> {
  const m = new Map<string, { to: string; name: string }>();
  for (const p of persons) if (p.primary) for (const o of p.merge) m.set(o.id, { to: p.primary.id, name: p.name });
  return m;
}

/** ① の下見(読むだけ)。まとめる人がいなければ何も読まない */
export async function scanRefs(persons: ReissuePerson[]): Promise<RefPlan> {
  const merge = mergeMapOf(persons);
  const plan: RefPlan = { writes: [], counts: {}, conflicts: [] };
  if (merge.size === 0) return plan;
  const idMap = new Map([...merge].map(([from, v]) => [from, v.to]));
  const db = getDb();
  const add = (col: string, w: Write) => {
    plan.writes.push(w);
    if (w.kind === 'set') plan.counts[col] = (plan.counts[col] ?? 0) + 1;
  };

  for (const col of VALUE_COLLECTIONS) {
    const snap = await getDocs(collection(db, col));
    for (const d of snap.docs) {
      const r = rewriteOpRefs(d.data(), idMap);
      if (r.conflict) plan.conflicts.push(`${REISSUE_COLLECTION_LABELS[col]} ${d.id}`);
      if (!r.changed || r.conflict) continue;
      const data = r.value as DocumentData;
      // 日次集計の行の名前も、まとめ先の名前にそろえる(交通費の精算などは名前で人を見分けるため)
      if (col === 'daily_summary' && Array.isArray(data.staff)) {
        const before = (d.data().staff ?? []) as { op_id?: string }[];
        data.staff = (data.staff as { op_id?: string; name?: string }[]).map((s, i) => {
          const from = before[i]?.op_id;
          const m = from ? merge.get(from) : undefined;
          return m ? { ...s, name: m.name } : s;
        });
      }
      add(col, { kind: 'set', col, id: d.id, data });
    }
  }

  for (const k of KEY_COLLECTIONS) {
    const snap = await getDocs(collection(db, k.name));
    const ids = new Set(snap.docs.map((d) => d.id));
    for (const d of snap.docs) {
      const { op, rebuild } = k.split(d.id);
      const to = idMap.get(op);
      if (!to) continue;
      const newId = rebuild(to);
      if (ids.has(newId)) {
        plan.conflicts.push(`${REISSUE_COLLECTION_LABELS[k.name]} ${d.id}(移し先 ${newId} がもうあります)`);
        continue;
      }
      const data = rewriteOpRefs(d.data(), idMap).value as DocumentData;
      add(k.name, { kind: 'set', col: k.name, id: newId, data });
      if (!('keepOld' in k && k.keepOld)) add(k.name, { kind: 'delete', col: k.name, id: d.id });
    }
  }
  return plan;
}

const BATCH = 400;

/** ①→② の順に書く。戻り値はマスタを書き換えた人数・新しく作った人数・まとめたマスタの数・書き換えたデータの件数 */
export async function applyReissue(persons: ReissuePerson[], refs: RefPlan, userId: string, existingCodes: string[]): Promise<{ updated: number; created: number; merged: number; refs: number }> {
  const db = getDb();
  // ① まとめる元を指しているデータ
  for (let i = 0; i < refs.writes.length; i += BATCH) {
    const batch = writeBatch(db);
    for (const w of refs.writes.slice(i, i + BATCH)) {
      if (w.kind === 'set') batch.set(doc(db, w.col, w.id), w.data);
      else batch.delete(doc(db, w.col, w.id));
    }
    await batch.commit();
  }

  // ② オペレーターマスタ(新しく作る人の管理番号は先に取る)
  // 念のため、登録済みの管理番号と重なったら次の番号へ進む
  const codes = new Map<number, string>();
  const usedCodes = new Set(existingCodes);
  for (const p of persons) {
    if (p.primary) continue;
    let code = await nextOperatorCode();
    while (usedCodes.has(code)) code = await nextOperatorCode();
    usedCodes.add(code);
    codes.set(p.no, code);
  }
  const now = serverTimestamp();
  const ops: { ref: ReturnType<typeof doc>; data: DocumentData; merge: boolean }[] = [];
  let updated = 0;
  let created = 0;
  let merged = 0;
  for (const p of persons) {
    const accounts = reissuedAccounts(p);
    if (p.primary) {
      ops.push({ ref: doc(db, 'operators', p.primary.id), data: { name: p.name, bb_accounts: accounts, ...primaryStyle(accounts), updated_by: userId, updated_at: now }, merge: true });
      updated += 1;
    } else {
      ops.push({
        ref: doc(collection(db, 'operators')),
        data: {
          op_code: codes.get(p.no)!,
          name: p.name,
          bb_accounts: accounts,
          ...primaryStyle(accounts),
          status: 'active',
          commute_cost: 0,
          commute_history: [],
          invoice_type: null,
          pc_rental: false,
          stats_visible: false,
          joined_at: null,
          left_at: null,
          extra_info: [],
          note: 'BB IDの付け直し(CSV)で登録',
          uid: null,
          created_by: userId,
          created_at: now,
          updated_by: userId,
          updated_at: now,
        },
        merge: false,
      });
      created += 1;
    }
    for (const m of p.merge) {
      ops.push({
        ref: doc(db, 'operators', m.id),
        data: {
          bb_accounts: [],
          status: 'retired',
          merged_into: p.primary!.id,
          note: [m.note, `BB IDの付け直しで「${p.name}」(${p.primary!.op_code})にまとめました`].filter(Boolean).join('\n'),
          // 本人ログインの紐付けもまとめ先へ移す
          ...(m.uid ? { uid: deleteField() } : {}),
          updated_by: userId,
          updated_at: now,
        },
        merge: true,
      });
      if (m.uid && !p.primary!.uid) ops.push({ ref: doc(db, 'operators', p.primary!.id), data: { uid: m.uid }, merge: true });
      merged += 1;
    }
  }
  for (let i = 0; i < ops.length; i += BATCH) {
    const batch = writeBatch(db);
    for (const o of ops.slice(i, i + BATCH)) batch.set(o.ref, o.data, { merge: o.merge });
    await batch.commit();
  }

  await writeAuditLog({
    targetType: 'operator',
    targetId: 'bb-reissue',
    action: 'operator-bb-reissue',
    by: userId,
    after: {
      persons: persons.length,
      updated,
      created,
      merged,
      refs: refs.writes.length,
      ids: Object.fromEntries(persons.flatMap((p) => p.items.filter((i) => i.oldBbId).map((i) => [i.oldBbId, i.newBbId]))),
    },
    changes: [`BB IDの付け直し: ${persons.length}名(マスタ更新 ${updated}・新規 ${created}・まとめ ${merged})`],
  });
  return { updated, created, merged, refs: refs.writes.length };
}

/** 書き込む前の控え(今のオペレーターマスタ全部を JSON で保存する) */
export function backupJson(operators: Operator[]): string {
  return JSON.stringify({ saved_at: new Date().toISOString(), operators }, null, 1);
}
